import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { payrollAPI, clientSettingsAPI, userAPI } from '../../services';
import { calculateProfessionalTax } from '../../utils/professionalTax';
import { generatePayslipPDF, printPayslipPDF } from './PayslipPDFWrapper';

// ESIC Calculation Helper - applicable only if gross salary <= Rs. 21,000
// Employee contribution: 0.75% of gross salary
const calculateESIC = (grossSalary) => {
  const ESIC_SALARY_LIMIT = 21000;
  const ESIC_RATE = 0.0075; // 0.75%
  
  if (grossSalary <= ESIC_SALARY_LIMIT) {
    return Math.round(grossSalary * ESIC_RATE);
  }
  return 0;
};

// Professional Tax Calculation Helper - state-wise slabs, see utils/professionalTax.js
const calculatePTax = (grossSalary, ptState) =>
  calculateProfessionalTax({ state: ptState, monthlyGross: grossSalary });

// Status colors
const statusColors = {
  draft: 'bg-gray-100 text-gray-800',
  generated: 'bg-blue-100 text-blue-800',
  approved: 'bg-yellow-100 text-yellow-800',
  paid: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};

const PayrollManagement = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('payslips');
  const [payslips, setPayslips] = useState([]);
  const [salaryConfigs, setSalaryConfigs] = useState([]);
  const [payrollStats, setPayrollStats] = useState(null);
  const [selectedPayslip, setSelectedPayslip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [clientData, setClientData] = useState(null);
  const [employeeDetails, setEmployeeDetails] = useState(null);

  // Export to Excel state
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportMonth, setExportMonth] = useState(new Date().getMonth() === 0 ? 12 : new Date().getMonth());
  const [exportYear, setExportYear] = useState(new Date().getMonth() === 0 ? new Date().getFullYear() - 1 : new Date().getFullYear());

  // Generate payslip form
  const [showGenerateForm, setShowGenerateForm] = useState(false);
  const [generateForm, setGenerateForm] = useState({
    employeeId: '',
    month: new Date().getMonth() === 0 ? 12 : new Date().getMonth(),
    year: new Date().getMonth() === 0 ? new Date().getFullYear() - 1 : new Date().getFullYear(),
    bonus: 0,
    incentive: 0,
  });

  // Salary config form
  const [showSalaryForm, setShowSalaryForm] = useState(false);
  const [salaryForm, setSalaryForm] = useState({
    employeeId: '',
    basicSalary: '',
    allowances: {
      hra: 0,
      da: 0,
      ta: 0,
      medical: 0,
      special: 0,
    },
    deductions: {
      pf: 0,
      esi: 0,
      tax: 0,
    },
  });

  const isAdmin = ['admin', 'superadmin'].includes(user?.role) || user?.permissions?.canGeneratePayroll === true;

  // Professional label mappings for earnings and deductions
  const earningLabels = {
    basic: 'Basic Salary',
    hra: 'House Rent Allowance (HRA)',
    da: 'Dearness Allowance (DA)',
    ta: 'Travel Allowance (TA)',
    medical: 'Medical Allowance',
    special: 'Special Allowance',
    overtime: 'Overtime Pay',
    bonus: 'Bonus',
    incentive: 'Incentive',
    other: 'Other Earnings',
  };

  const deductionLabels = {
    pf: 'Provident Fund (PF)',
    esi: 'ESIC (Employee)',
    tax: 'Professional Tax',
    tds: 'TDS',
    advance: 'Salary Advance',
    other: 'Other Deductions',
    loan: 'Loan Recovery',
    lop: 'Loss of Pay (LOP)',
  };

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Company-wide professional tax defaults. The salary-config form only knows an
  // employee ID, not the employee record, so the preview here uses the company
  // default; payroll resolves the employee's own state when generating.
  const ptEnabledForClient = clientData?.payrollSettings?.professionalTax?.enabled;
  const ptDefaultState = ptEnabledForClient === false
    ? ''
    : (clientData?.payrollSettings?.professionalTax?.defaultState || '');

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  // Client settings hold the company-wide professional tax defaults.
  useEffect(() => {
    let cancelled = false;
    clientSettingsAPI.getMySettings()
      .then((res) => { if (!cancelled && res?.data) setClientData(res.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      if (activeTab === 'payslips') {
        const response = await payrollAPI.getPayslips();
        setPayslips(response.data || []);
      } else if (activeTab === 'salary-configs' && isAdmin) {
        const response = await payrollAPI.getAllSalaryConfigs();
        setSalaryConfigs(response.data || []);
      } else if (activeTab === 'stats' && isAdmin) {
        const response = await payrollAPI.getPayrollStats();
        setPayrollStats(response.data);
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleViewPayslip = async (id) => {
    try {
      const response = await payrollAPI.getPayslip(id);
      setSelectedPayslip(response.data);
      
      // Fetch client data for PDF using my-settings endpoint
      try {
        const clientResponse = await clientSettingsAPI.getMySettings();
        setClientData(clientResponse.data);
      } catch (err) {
        console.log('Could not fetch client data:', err);
      }
      
      // Fetch employee details for PDF
      if (response.data?.employee?._id) {
        try {
          // For employees viewing their own payslip, use the populated data from payslip
          // For admins, fetch full employee details
          if (isAdmin) {
            const employeeResponse = await userAPI.getUser(response.data.employee._id);
            setEmployeeDetails(employeeResponse.data);
          } else {
            // For employees, use the populated employee data from payslip or current user data
            const isOwnPayslip = response.data.employee._id === user._id;
            if (isOwnPayslip) {
              // Use current user data merged with payslip employee data
              setEmployeeDetails({
                ...response.data.employee,
                name: user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim(),
                firstName: user.firstName,
                lastName: user.lastName,
                employeeId: user.employeeId,
                designation: user.designation,
                dateOfJoining: user.dateOfJoining,
                department: user.department,
                salary: user.salary,
                statutory: user.statutory,
                payment: user.payment,
              });
            } else {
              setEmployeeDetails(response.data.employee);
            }
          }
        } catch (err) {
          console.log('Could not fetch employee details:', err);
          // Fallback to using the current user's data if it's their own payslip
          if (response.data.employee._id === user._id) {
            setEmployeeDetails({
              ...response.data.employee,
              name: user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim(),
              firstName: user.firstName,
              lastName: user.lastName,
              employeeId: user.employeeId,
              designation: user.designation,
              dateOfJoining: user.dateOfJoining,
            });
          }
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to load payslip');
    }
  };

  // Handle PDF download
  const handleDownloadPDF = async () => {
    if (!selectedPayslip) return;
    
    setPdfLoading(true);
    setError('');
    
    try {
      // Ensure client data is loaded
      let localClientData = clientData;
      if (!localClientData) {
        try {
          const clientResponse = await clientSettingsAPI.getMySettings();
          localClientData = clientResponse.data;
          setClientData(localClientData);
        } catch (clientErr) {
          console.log('Could not fetch client data:', clientErr);
        }
      }
      
      const result = await generatePayslipPDF(
        selectedPayslip,
        employeeDetails || selectedPayslip.employee,
        localClientData
      );
      
      if (result.success) {
        setSuccess(`Payslip PDF downloaded: ${result.fileName}`);
      } else {
        setError('Failed to generate PDF: ' + result.error);
      }
    } catch (err) {
      setError('Failed to generate PDF: ' + err.message);
    } finally {
      setPdfLoading(false);
    }
  };

  // Handle PDF print
  const handlePrintPDF = async () => {
    if (!selectedPayslip) return;
    
    setPdfLoading(true);
    setError('');
    
    try {
      // Ensure client data is loaded
      let localClientData = clientData;
      if (!localClientData) {
        try {
          const clientResponse = await clientSettingsAPI.getMySettings();
          localClientData = clientResponse.data;
          setClientData(localClientData);
        } catch (clientErr) {
          console.log('Could not fetch client data:', clientErr);
        }
      }
      
      const result = await printPayslipPDF(
        selectedPayslip,
        employeeDetails || selectedPayslip.employee,
        localClientData
      );
      
      if (!result.success) {
        setError('Failed to print PDF: ' + result.error);
      }
    } catch (err) {
      setError('Failed to print PDF: ' + err.message);
    } finally {
      setPdfLoading(false);
    }
  };

  // Handle PDF download directly from card (without opening detail modal)
  const handleCardDownloadPDF = async (payslipId) => {
    setPdfLoading(true);
    setError('');
    
    try {
      // Fetch payslip details
      const response = await payrollAPI.getPayslip(payslipId);
      const payslipData = response.data;
      
      // Fetch client data
      let localClientData = clientData;
      if (!localClientData) {
        try {
          const clientResponse = await clientSettingsAPI.getMySettings();
          localClientData = clientResponse.data;
          setClientData(localClientData);
        } catch (clientErr) {
          console.log('Could not fetch client data:', clientErr);
        }
      }
      
      // Fetch/build employee details
      let empDetails = null;
      if (payslipData?.employee?._id) {
        if (isAdmin) {
          try {
            const employeeResponse = await userAPI.getUser(payslipData.employee._id);
            empDetails = employeeResponse.data;
          } catch (err) {
            empDetails = payslipData.employee;
          }
        } else if (payslipData.employee._id === user._id) {
          empDetails = {
            ...payslipData.employee,
            name: user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim(),
            firstName: user.firstName,
            lastName: user.lastName,
            employeeId: user.employeeId,
            designation: user.designation,
            dateOfJoining: user.dateOfJoining,
            department: user.department,
            salary: user.salary,
            statutory: user.statutory,
            payment: user.payment,
          };
        } else {
          empDetails = payslipData.employee;
        }
      }
      
      const result = await generatePayslipPDF(
        payslipData,
        empDetails || payslipData.employee,
        localClientData
      );
      
      if (result.success) {
        setSuccess(`Payslip PDF downloaded: ${result.fileName}`);
      } else {
        setError('Failed to generate PDF: ' + result.error);
      }
    } catch (err) {
      setError('Failed to download PDF: ' + err.message);
    } finally {
      setPdfLoading(false);
    }
  };

  // Handle email payslip from detail modal
  const handleEmailPayslip = async () => {
    if (!selectedPayslip) return;
    
    setEmailLoading(true);
    setError('');
    
    try {
      const response = await payrollAPI.emailPayslip(selectedPayslip._id);
      if (response.status === 'success') {
        setSuccess(response.message || 'Payslip sent successfully via email');
      } else {
        setError('Failed to send payslip email');
      }
    } catch (err) {
      setError('Failed to send email: ' + (err.response?.data?.message || err.message));
    } finally {
      setEmailLoading(false);
    }
  };

  // Handle email payslip directly from card
  const handleCardEmailPayslip = async (payslipId) => {
    setEmailLoading(true);
    setError('');
    
    try {
      const response = await payrollAPI.emailPayslip(payslipId);
      if (response.status === 'success') {
        setSuccess(response.message || 'Payslip sent successfully via email');
      } else {
        setError('Failed to send payslip email');
      }
    } catch (err) {
      setError('Failed to send email: ' + (err.response?.data?.message || err.message));
    } finally {
      setEmailLoading(false);
    }
  };

  const handleGeneratePayslip = async (e) => {
    e.preventDefault();
    setError('');

    // Prevent generating payslip for current or future months
    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const currentYear = now.getFullYear();
    if (generateForm.year > currentYear || (generateForm.year === currentYear && generateForm.month >= currentMonth)) {
      setError('Payslips can only be generated for past months. Current and future month payslips are not allowed.');
      return;
    }

    try {
      await payrollAPI.generatePayslip(generateForm);
      setSuccess('Payslip generated successfully!');
      setShowGenerateForm(false);
      fetchData();
    } catch (err) {
      setError(err.message || 'Failed to generate payslip');
    }
  };

  const handleApprovePayslip = async (id) => {
    try {
      await payrollAPI.approvePayslip(id);
      setSuccess('Payslip approved!');
      fetchData();
      if (selectedPayslip?._id === id) {
        setSelectedPayslip({ ...selectedPayslip, status: 'approved' });
      }
    } catch (err) {
      setError(err.message || 'Failed to approve payslip');
    }
  };

  const handleMarkAsPaid = async (id) => {
    try {
      await payrollAPI.markAsPaid(id, { paymentMode: 'bank_transfer' });
      setSuccess('Payslip marked as paid!');
      fetchData();
      if (selectedPayslip?._id === id) {
        setSelectedPayslip({ ...selectedPayslip, status: 'paid' });
      }
    } catch (err) {
      setError(err.message || 'Failed to mark as paid');
    }
  };

  const handleCreateSalaryConfig = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await payrollAPI.createSalaryConfig(salaryForm);
      setSuccess('Salary configuration created!');
      setShowSalaryForm(false);
      setSalaryForm({
        employeeId: '',
        basicSalary: '',
        allowances: { hra: 0, da: 0, ta: 0, medical: 0, special: 0 },
        deductions: { pf: 0, esi: 0, tax: 0 },
      });
      fetchData();
    } catch (err) {
      setError(err.message || 'Failed to create salary config');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Handle export to Excel
  const handleExportToExcel = async () => {
    setExportLoading(true);
    setError('');
    try {
      await payrollAPI.exportToExcel(exportMonth, exportYear);
      setSuccess(`Payroll exported successfully for ${months[exportMonth - 1]} ${exportYear}`);
      setShowExportModal(false);
    } catch (err) {
      setError(err.message || 'Failed to export payroll');
    } finally {
      setExportLoading(false);
    }
  };

  // Handle export single employee to Excel
  const handleExportSingleEmployee = async (payslip) => {
    setExportLoading(true);
    setError('');
    try {
      const employeeName = payslip.employee?.name || payslip.employee?.firstName || 'Employee';
      await payrollAPI.exportSingleEmployeeToExcel(
        payslip.month, 
        payslip.year, 
        payslip.employee._id || payslip.employee,
        employeeName
      );
      setSuccess(`Payroll exported successfully for ${employeeName}`);
    } catch (err) {
      setError(err.message || 'Failed to export employee payroll');
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Payroll Management</h1>
        <p className="text-gray-600">View payslips and manage salary configurations</p>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
          <button onClick={() => setError('')} className="float-right font-bold">×</button>
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
          {success}
          <button onClick={() => setSuccess('')} className="float-right font-bold">×</button>
        </div>
      )}

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('payslips')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'payslips'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            My Payslips
          </button>
          {isAdmin && (
            <>
              <button
                onClick={() => setActiveTab('salary-configs')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'salary-configs'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Salary Configs
              </button>
              <button
                onClick={() => setActiveTab('stats')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'stats'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Payroll Stats
              </button>
            </>
          )}
        </nav>
      </div>

      {/* Admin Actions */}
      {isAdmin && (
        <div className="mb-6 flex gap-3 flex-wrap">
          <button
            onClick={() => setShowSalaryForm(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Salary Config
          </button>
          <button
            onClick={() => setShowGenerateForm(true)}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Generate Payslip
          </button>
          <button
            onClick={() => setShowExportModal(true)}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Export to Excel
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <>
          {/* Payslips Tab */}
          {activeTab === 'payslips' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {payslips.length === 0 ? (
                <div className="col-span-full text-center py-12 text-gray-500">
                  No payslips found
                </div>
              ) : (
                payslips.map((payslip) => (
                  <div
                    key={payslip._id}
                    className="bg-white rounded-xl shadow-sm border p-6 hover:shadow-md transition-shadow"
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="font-semibold text-lg text-gray-900">
                          {months[payslip.month - 1]} {payslip.year}
                        </h3>
                        {isAdmin && payslip.employee && (
                          <p className="text-sm text-gray-500">{payslip.employee.name}</p>
                        )}
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${statusColors[payslip.status]}`}>
                        {payslip.status}
                      </span>
                    </div>
                    
                    <div className="space-y-2 mb-4">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Gross Earnings</span>
                        <span className="text-green-600 font-medium">{formatCurrency(payslip.grossEarnings)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Deductions</span>
                        <span className="text-red-600 font-medium">-{formatCurrency(payslip.totalDeductions)}</span>
                      </div>
                      <div className="border-t pt-2 flex justify-between">
                        <span className="font-medium text-gray-700">Net Payable</span>
                        <span className="font-bold text-blue-600">{formatCurrency(payslip.netPayable)}</span>
                      </div>
                    </div>

                    <div className="text-xs text-gray-400 mb-3">
                      Days Worked: {payslip.attendance?.presentDays || 0} / {payslip.attendance?.workingDays || 0}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2 pt-3 border-t">
                      <button
                        onClick={() => handleViewPayslip(payslip._id)}
                        className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        View
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleExportSingleEmployee(payslip); }}
                        disabled={exportLoading}
                        className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Export to Excel"
                      >
                        {exportLoading ? (
                          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        )}
                        Excel
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleCardEmailPayslip(payslip._id); }}
                        disabled={emailLoading}
                        className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {emailLoading ? (
                          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        )}
                        Email
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Salary Configs Tab */}
          {activeTab === 'salary-configs' && isAdmin && (
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Basic Salary</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Allowances</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Deductions</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Net Salary</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {salaryConfigs.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                        No salary configurations found
                      </td>
                    </tr>
                  ) : (
                    salaryConfigs.map((config) => {
                      const allowanceTotal = Object.values(config.allowances || {}).reduce((a, b) => a + b, 0);
                      const deductionTotal = Object.values(config.deductions || {}).reduce((a, b) => a + b, 0);
                      const netSalary = config.basicSalary + allowanceTotal - deductionTotal;
                      
                      return (
                        <tr key={config._id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="flex items-center">
                              <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                                <span className="text-blue-600 font-medium">
                                  {config.employee?.name?.charAt(0) || 'U'}
                                </span>
                              </div>
                              <div className="ml-3">
                                <p className="text-sm font-medium text-gray-900">{config.employee?.name}</p>
                                <p className="text-xs text-gray-500">{config.employee?.department}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">
                            {formatCurrency(config.basicSalary)}
                          </td>
                          <td className="px-6 py-4 text-sm text-green-600">
                            +{formatCurrency(allowanceTotal)}
                          </td>
                          <td className="px-6 py-4 text-sm text-red-600">
                            -{formatCurrency(deductionTotal)}
                          </td>
                          <td className="px-6 py-4 text-sm font-bold text-blue-600">
                            {formatCurrency(netSalary)}
                          </td>
                          <td className="px-6 py-4">
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 capitalize">
                              {config.salaryType}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Stats Tab */}
          {activeTab === 'stats' && isAdmin && payrollStats && (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl shadow-sm border p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total Employees</p>
                      <p className="text-2xl font-bold text-gray-900">{payrollStats.totalEmployees}</p>
                    </div>
                    <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-2xl">👥</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total Gross</p>
                      <p className="text-2xl font-bold text-green-600">{formatCurrency(payrollStats.totalGross)}</p>
                    </div>
                    <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="text-2xl">💰</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total Deductions</p>
                      <p className="text-2xl font-bold text-red-600">{formatCurrency(payrollStats.totalDeductions)}</p>
                    </div>
                    <div className="h-12 w-12 bg-red-100 rounded-full flex items-center justify-center">
                      <span className="text-2xl">📉</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Net Payable</p>
                      <p className="text-2xl font-bold text-blue-600">{formatCurrency(payrollStats.totalNet)}</p>
                    </div>
                    <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-2xl">💵</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Status Breakdown */}
              <div className="bg-white rounded-xl shadow-sm border p-6">
                <h3 className="font-semibold text-lg mb-4">Payslip Status for {months[payrollStats.month - 1]} {payrollStats.year}</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <p className="text-3xl font-bold text-gray-600">{payrollStats.byStatus?.draft || 0}</p>
                    <p className="text-sm text-gray-500">Draft</p>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <p className="text-3xl font-bold text-blue-600">{payrollStats.byStatus?.generated || 0}</p>
                    <p className="text-sm text-gray-500">Generated</p>
                  </div>
                  <div className="text-center p-4 bg-yellow-50 rounded-lg">
                    <p className="text-3xl font-bold text-yellow-600">{payrollStats.byStatus?.approved || 0}</p>
                    <p className="text-sm text-gray-500">Approved</p>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <p className="text-3xl font-bold text-green-600">{payrollStats.byStatus?.paid || 0}</p>
                    <p className="text-sm text-gray-500">Paid</p>
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-2 gap-4">
                  <div className="p-4 bg-yellow-50 rounded-lg">
                    <p className="text-sm text-gray-600">Pending Payment</p>
                    <p className="text-xl font-bold text-yellow-700">{formatCurrency(payrollStats.pendingAmount)}</p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg">
                    <p className="text-sm text-gray-600">Already Paid</p>
                    <p className="text-xl font-bold text-green-700">{formatCurrency(payrollStats.paidAmount)}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Payslip Detail Modal */}
      {selectedPayslip && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 my-8 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-xl font-bold">Payslip</h2>
                  <p className="text-gray-500">{months[selectedPayslip.month - 1]} {selectedPayslip.year}</p>
                </div>
                <button onClick={() => setSelectedPayslip(null)} className="text-gray-400 hover:text-gray-600">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Employee Info */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Employee Name</p>
                    <p className="font-medium text-gray-900">{selectedPayslip.employee?.name || (employeeDetails?.firstName ? `${employeeDetails.firstName} ${employeeDetails.lastName || ''}`.trim() : 'N/A')}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Employee ID</p>
                    <p className="font-medium text-gray-900">{employeeDetails?.employeeId || selectedPayslip.employee?.employeeId || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Designation</p>
                    <p className="font-medium text-gray-900">{employeeDetails?.designation || selectedPayslip.employee?.designation || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Department</p>
                    <p className="font-medium text-gray-900">{selectedPayslip.employee?.department || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Pay Period</p>
                    <p className="font-medium text-gray-900">{selectedPayslip.payPeriod}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Status</p>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${statusColors[selectedPayslip.status]}`}>
                      {selectedPayslip.status}
                    </span>
                  </div>
                </div>
              </div>

              {/* Attendance Summary */}
              <div className="mb-6">
                <h3 className="font-semibold mb-3">Attendance Summary</h3>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 text-center">
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <p className="text-lg font-bold text-blue-600">{selectedPayslip.attendance?.workingDays || 0}</p>
                    <p className="text-xs text-gray-500">Working Days</p>
                  </div>
                  <div className="bg-green-50 p-3 rounded-lg">
                    <p className="text-lg font-bold text-green-600">{selectedPayslip.attendance?.presentDays || 0}</p>
                    <p className="text-xs text-gray-500">Present</p>
                  </div>
                  <div className="bg-orange-50 p-3 rounded-lg">
                    <p className="text-lg font-bold text-orange-600">{selectedPayslip.attendance?.halfDays || 0}</p>
                    <p className="text-xs text-gray-500">Half Day</p>
                  </div>
                  <div className="bg-red-50 p-3 rounded-lg">
                    <p className="text-lg font-bold text-red-600">{selectedPayslip.attendance?.lossOfPay || 0}</p>
                    <p className="text-xs text-gray-500">LOP</p>
                  </div>
                  <div className="bg-yellow-50 p-3 rounded-lg">
                    <p className="text-lg font-bold text-yellow-600">{selectedPayslip.attendance?.leaveDays || 0}</p>
                    <p className="text-xs text-gray-500">Leave</p>
                  </div>
                  <div className="bg-pink-50 p-3 rounded-lg">
                    <p className="text-lg font-bold text-pink-600">{selectedPayslip.attendance?.absentDays || 0}</p>
                    <p className="text-xs text-gray-500">Absent</p>
                  </div>
                </div>
              </div>

              {/* Earnings & Deductions */}
              <div className="grid grid-cols-2 gap-6 mb-6">
                {/* Earnings */}
                <div className="bg-green-50 rounded-lg p-4">
                  <h3 className="font-semibold mb-3 text-green-700 border-b border-green-200 pb-2">Earnings</h3>
                  <div className="space-y-2">
                    {Object.entries(selectedPayslip.earnings || {}).map(([key, value]) => (
                      value > 0 && (
                        <div key={key} className="flex justify-between text-sm">
                          <span className="text-gray-700">{earningLabels[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}</span>
                          <span className="text-green-700 font-medium">{formatCurrency(value)}</span>
                        </div>
                      )
                    ))}
                    <div className="border-t border-green-200 pt-2 flex justify-between font-semibold">
                      <span>Gross Earnings</span>
                      <span className="text-green-700">{formatCurrency(selectedPayslip.grossEarnings)}</span>
                    </div>
                  </div>
                </div>

                {/* Deductions */}
                <div className="bg-red-50 rounded-lg p-4">
                  <h3 className="font-semibold mb-3 text-red-700 border-b border-red-200 pb-2">Deductions</h3>
                  <div className="space-y-2">
                    {Object.entries(selectedPayslip.deductions || {}).map(([key, value]) => (
                      value > 0 && (
                        <div key={key} className="flex justify-between text-sm">
                          <span className="text-gray-700">{deductionLabels[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}</span>
                          <span className="text-red-700 font-medium">-{formatCurrency(value)}</span>
                        </div>
                      )
                    ))}
                    <div className="border-t border-red-200 pt-2 flex justify-between font-semibold">
                      <span>Total Deductions</span>
                      <span className="text-red-700">-{formatCurrency(selectedPayslip.totalDeductions)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Net Payable */}
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg p-4 mb-6">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold text-white">Net Payable</span>
                  <span className="text-2xl font-bold text-white">{formatCurrency(selectedPayslip.netPayable)}</span>
                </div>
              </div>

              {/* Bank & Statutory Info */}
              {(employeeDetails?.payment?.bankDetails || employeeDetails?.statutory) && (
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <h3 className="font-semibold mb-3 text-gray-700 text-sm">Payment & Statutory Details</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                    {employeeDetails?.payment?.bankDetails?.accountNumber && (
                      <div>
                        <p className="text-xs text-gray-500">Bank A/C</p>
                        <p className="font-medium text-gray-700">****{employeeDetails.payment.bankDetails.accountNumber.slice(-4)}</p>
                      </div>
                    )}
                    {employeeDetails?.payment?.bankDetails?.bankName && (
                      <div>
                        <p className="text-xs text-gray-500">Bank Name</p>
                        <p className="font-medium text-gray-700">{employeeDetails.payment.bankDetails.bankName}</p>
                      </div>
                    )}
                    {employeeDetails?.payment?.bankDetails?.ifscCode && (
                      <div>
                        <p className="text-xs text-gray-500">IFSC Code</p>
                        <p className="font-medium text-gray-700">{employeeDetails.payment.bankDetails.ifscCode}</p>
                      </div>
                    )}
                    {employeeDetails?.personal?.pan && (
                      <div>
                        <p className="text-xs text-gray-500">PAN</p>
                        <p className="font-medium text-gray-700">{employeeDetails.personal.pan}</p>
                      </div>
                    )}
                    {employeeDetails?.statutory?.uan && (
                      <div>
                        <p className="text-xs text-gray-500">UAN</p>
                        <p className="font-medium text-gray-700">{employeeDetails.statutory.uan}</p>
                      </div>
                    )}
                    {employeeDetails?.statutory?.esiNumber && (
                      <div>
                        <p className="text-xs text-gray-500">ESI Number</p>
                        <p className="font-medium text-gray-700">{employeeDetails.statutory.esiNumber}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Send Email Button */}
              <div className="mb-4">
                <button
                  onClick={handleEmailPayslip}
                  disabled={emailLoading}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-green-400 disabled:cursor-not-allowed"
                >
                  {emailLoading ? (
                    <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  )}
                  {emailLoading ? 'Sending Email...' : 'Send Payslip via Email'}
                </button>
              </div>

              {/* Admin Actions */}
              {isAdmin && (
                <div className="flex gap-3">
                  {selectedPayslip.status === 'generated' && (
                    <button
                      onClick={() => handleApprovePayslip(selectedPayslip._id)}
                      className="flex-1 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
                    >
                      Approve Payslip
                    </button>
                  )}
                  {selectedPayslip.status === 'approved' && (
                    <button
                      onClick={() => handleMarkAsPaid(selectedPayslip._id)}
                      className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                    >
                      Mark as Paid
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Generate Payslip Modal */}
      {showGenerateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Generate Payslip</h2>
              <button onClick={() => setShowGenerateForm(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleGeneratePayslip} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Employee ID</label>
                <input
                  type="text"
                  value={generateForm.employeeId}
                  onChange={(e) => setGenerateForm({ ...generateForm, employeeId: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter employee ID"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
                  <select
                    value={generateForm.month}
                    onChange={(e) => setGenerateForm({ ...generateForm, month: parseInt(e.target.value) })}
                    className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {months.map((m, i) => {
                      const mVal = i + 1;
                      const isDisabled = generateForm.year > new Date().getFullYear() || (generateForm.year === new Date().getFullYear() && mVal >= new Date().getMonth() + 1);
                      return (
                        <option key={m} value={mVal} disabled={isDisabled}>
                          {m}{isDisabled ? ' (Not available)' : ''}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                  <input
                    type="number"
                    value={generateForm.year}
                    onChange={(e) => setGenerateForm({ ...generateForm, year: parseInt(e.target.value) })}
                    className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min={2020}
                    max={2030}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bonus</label>
                  <input
                    type="number"
                    value={generateForm.bonus}
                    onChange={(e) => setGenerateForm({ ...generateForm, bonus: parseFloat(e.target.value) || 0 })}
                    className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min={0}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Incentive</label>
                  <input
                    type="number"
                    value={generateForm.incentive}
                    onChange={(e) => setGenerateForm({ ...generateForm, incentive: parseFloat(e.target.value) || 0 })}
                    className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min={0}
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowGenerateForm(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Generate
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Export to Excel Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Export Payroll to Excel</h2>
              <button onClick={() => setShowExportModal(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <p className="text-gray-600 mb-4">
              Export attendance and payroll data for all employees. The Excel sheet will include daily attendance, 
              earnings, deductions, and employee details.
            </p>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
                  <select
                    value={exportMonth}
                    onChange={(e) => setExportMonth(parseInt(e.target.value))}
                    className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    {months.map((m, i) => (
                      <option key={m} value={i + 1}>{m}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                  <input
                    type="number"
                    value={exportYear}
                    onChange={(e) => setExportYear(parseInt(e.target.value))}
                    className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    min={2020}
                    max={2030}
                  />
                </div>
              </div>

              <div className="bg-purple-50 p-3 rounded-lg text-sm text-purple-700">
                <p className="font-medium">Pay Period:</p>
                <p>26-{String(exportMonth === 1 ? 12 : exportMonth - 1).padStart(2, '0')}-{exportMonth === 1 ? exportYear - 1 : exportYear} to 25-{String(exportMonth).padStart(2, '0')}-{exportYear}</p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowExportModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  disabled={exportLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleExportToExcel}
                  disabled={exportLoading}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {exportLoading ? (
                    <>
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Exporting...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Export Excel
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Salary Config Modal */}
      {showSalaryForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 my-8 p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Add Salary Configuration</h2>
              <button onClick={() => setShowSalaryForm(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleCreateSalaryConfig} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Employee ID</label>
                <input
                  type="text"
                  value={salaryForm.employeeId}
                  onChange={(e) => setSalaryForm({ ...salaryForm, employeeId: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter employee ID"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Basic Salary (₹)</label>
                <input
                  type="number"
                  value={salaryForm.basicSalary}
                  onChange={(e) => setSalaryForm({ ...salaryForm, basicSalary: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., 30000"
                  required
                />
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Allowances</h3>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(salaryForm.allowances).map(([key, value]) => (
                    <div key={key}>
                      <label className="block text-xs text-gray-500 mb-1 uppercase">{key}</label>
                      <input
                        type="number"
                        value={value}
                        onChange={(e) => setSalaryForm({
                          ...salaryForm,
                          allowances: { ...salaryForm.allowances, [key]: parseFloat(e.target.value) || 0 }
                        })}
                        className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Deductions</h3>
                {/* Auto-calculation info */}
                {(() => {
                  const grossSalary = parseFloat(salaryForm.basicSalary || 0) + 
                    Object.values(salaryForm.allowances).reduce((a, b) => a + b, 0);
                  const autoESIC = calculateESIC(grossSalary);
                  const ptTax = calculatePTax(grossSalary, ptDefaultState);
                  
                  return (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3 text-sm">
                      <p className="text-blue-800 font-medium mb-2">Auto-calculated based on Gross Salary: ₹{grossSalary.toLocaleString()}</p>
                      <div className="grid grid-cols-2 gap-2 text-blue-700">
                        <div>
                          <span className="font-medium">ESIC:</span> ₹{autoESIC} 
                          <span className="text-xs block text-blue-600">
                            {grossSalary <= 21000 ? '(0.75% - Salary ≤ ₹21,000)' : '(Not applicable - Salary > ₹21,000)'}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium">P.Tax:</span> ₹{ptTax.amount}
                          <span className="text-xs block text-blue-600">
                            {ptEnabledForClient === false
                              ? '(Professional tax is off in payroll settings)'
                              : !ptDefaultState
                                ? '(No default PT state set in payroll settings)'
                                : ptTax.applicable
                                  ? `(${ptTax.stateName} · ₹${ptTax.annualAmount.toLocaleString('en-IN')} per year)`
                                  : `(${ptTax.reason})`}
                          </span>
                        </div>
                      </div>
                      <p className="text-xs text-blue-600 mt-2">
                        P.Tax shown is the company default. On the payslip it is recalculated from the
                        employee's own PT state where one is set.
                      </p>
                    </div>
                  );
                })()}
                <div className="grid grid-cols-3 gap-3">
                  {Object.entries(salaryForm.deductions).map(([key, value]) => {
                    const grossSalary = parseFloat(salaryForm.basicSalary || 0) + 
                      Object.values(salaryForm.allowances).reduce((a, b) => a + b, 0);
                    const isESI = key === 'esi';
                    const isTax = key === 'tax';
                    const autoValue = isESI ? calculateESIC(grossSalary) : (isTax ? calculatePTax(grossSalary, ptDefaultState).amount : 0);
                    
                    return (
                      <div key={key}>
                        <label className="block text-xs text-gray-500 mb-1 uppercase">
                          {key === 'tax' ? 'P.Tax' : key}
                          {(isESI || isTax) && <span className="text-blue-500 ml-1">(Auto)</span>}
                        </label>
                        <input
                          type="number"
                          value={(isESI || isTax) ? autoValue : value}
                          onChange={(e) => setSalaryForm({
                            ...salaryForm,
                            deductions: { ...salaryForm.deductions, [key]: parseFloat(e.target.value) || 0 }
                          })}
                          className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${(isESI || isTax) ? 'bg-gray-100' : ''}`}
                          readOnly={isESI || isTax}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowSalaryForm(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Save Configuration
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayrollManagement;
